import { useMemo } from 'react'
import { useSelector } from 'react-redux'

/**
 * The signed-in user, read in ONE place.
 *
 * `localStorage.getItem("userDetails")` is currently parsed in 311 files (405
 * call sites), each re-implementing the same null handling and role checks. This
 * hook is the single supported way to read that state from now on; existing call
 * sites are being migrated opportunistically rather than in one risky sweep
 * (see docs/FRONTEND-STRUCTURE.md FC1).
 *
 * ── Security note ─────────────────────────────────────────────────────────────
 * Everything here is DISPLAY state. It is not a security boundary and must never
 * be treated as one. The API derives the caller's identity and role from the
 * signed JWT (`BaseApiController.CurrentUserId` / `ResolveOwnerId`), and ignores
 * any user id the client sends, so editing `userDetails` in devtools changes
 * what this browser renders and nothing about what the server will allow.
 *
 * Use `useAuthority` for route/feature gating, and never rely on these roles for
 * anything the server has not independently checked.
 */

/** The localStorage key the signed-in user is stored under. */
export const USER_DETAILS_KEY = 'userDetails'

/**
 * The stored user as its raw JSON string (or null). For code that cannot call
 * the hook -- event handlers, thunks, services, module scope.
 */
export const getUserDetailsJSON = () => localStorage.getItem(USER_DETAILS_KEY)

/**
 * The stored user, parsed. Exactly what `JSON.parse(localStorage.getItem(
 * 'userDetails'))` returned at every call site it replaced, so behaviour is
 * unchanged; the difference is that there is now one place that reads it.
 */
export const getUserDetails = () => JSON.parse(getUserDetailsJSON())

const readUserDetails = () => {
    try {
        const raw = getUserDetailsJSON()
        if (!raw) return null
        const parsed = JSON.parse(raw)
        // The API returns { status: "Error", ... } on failure using the legacy
        // 200-with-error-body contract; that is not a user object.
        if (!parsed || parsed.status === 'Error') return null
        return parsed
    } catch (e) {
        // Corrupt JSON or storage disabled — behave as signed out rather than
        // throwing during render.
        return null
    }
}

/**
 * @returns {{
 *   userDetails: object|null,
 *   userData: object|null,
 *   userId: string|null,
 *   roles: string[],
 *   primaryRole: string|null,
 *   isAdmin: boolean,
 *   isClient: boolean,
 *   signedIn: boolean,
 *   hasRole: (...names: string[]) => boolean
 * }}
 */
const useCurrentUser = () => {
    // Re-read when the session token changes (sign-in / refresh / sign-out) so
    // the value cannot go stale for the life of the page.
    const token = useSelector((state) => state.auth?.session?.token)
    const signedIn = useSelector((state) => state.auth?.session?.signedIn)

    return useMemo(() => {
        const userDetails = readUserDetails()
        const userData = userDetails?.userData ?? null
        const roles = Array.isArray(userDetails?.userRoles)
            ? userDetails.userRoles
            : []

        const hasRole = (...names) => names.some((n) => roles.includes(n))

        return {
            userDetails,
            userData,
            // The ownership model is inconsistent server-side (userdId / userId /
            // id), so check the spellings that actually occur.
            userId: userData?.userdId ?? userData?.userId ?? userData?.id ?? null,
            roles,
            primaryRole: roles[0] ?? null,
            isAdmin: hasRole('Admin', 'Company Admin', 'Administrator'),
            isClient: hasRole('Client'),
            signedIn: Boolean(signedIn && userDetails),
            hasRole,
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, signedIn])
}

export default useCurrentUser
